import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { MiniPitch } from "@/components/FootballAccents";
import ReviewPrompt from "@/components/ReviewPrompt";
import ProfileVisibilityCard from "@/components/profile/ProfileVisibilityCard";
import DreamPricingTiers from "@/components/DreamPricingTiers";
import ReferralInviteCard from "@/components/ReferralInviteCard";
import FeatureConsentCard from "@/components/FeatureConsentCard";
import DashboardHero from "@/components/dashboard/DashboardHero";
import PerformancePanel from "@/components/dashboard/PerformancePanel";
import ScoutPreviewUpsell from "@/components/dashboard/ScoutPreviewUpsell";
import InboxPanels from "@/components/dashboard/InboxPanels";
import CommunityPulse from "@/components/dashboard/CommunityPulse";
import OpportunitiesPanel from "@/components/dashboard/OpportunitiesPanel";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { trackPurchase } from "@/lib/pixels";
import {
  Plus, Lock, CheckCircle2, Film, Loader2, TrendingUp,
  Activity, ArrowRight, Sparkles, Crown, Calendar, XCircle, RefreshCw,
  Globe2, Copy, Eye,
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [players, setPlayers] = useState([]);
  // Legacy Progress Pass status — still queried so we can show
  // remaining credits to users who bought the $399 pass before
  // subscriptions launched. New users never see the buy flow.
  const [passState, setPassState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Subscription state — { subscription: {...}, tiers: {...}, usage: {...} } from /api/me/subscription
  const [subscription, setSubscription] = useState(null);
  const [tiers, setTiers] = useState({});
  // Monthly upload usage — drives the "Premium at limit → VIP only" banner variant
  const [usage, setUsage] = useState(null);
  // Admin-controlled ScoutMePlay Network numbers
  const [community, setCommunity] = useState(null);
  // Profile views (scout database detail opens) — premium only
  const [profileViews, setProfileViews] = useState(null);

  const fetchAll = () => {
    Promise.allSettled([
      api.get("/reports/mine").then(({ data }) => setReports(data)),
      api.get("/progress/players").then(({ data }) => setPlayers(data.items || [])),
      api.get("/progress/pass/status").then(({ data }) => setPassState(data)),
      api.get("/dashboard/community").then(({ data }) => setCommunity(data)),
      api.get("/dashboard/profile-views").then(({ data }) => setProfileViews(data)),
      api.get("/me/subscription").then(({ data }) => {
        setSubscription(data.subscription);
        setTiers(data.tiers || {});
        setUsage(data.usage || null);
      }),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    // Paid scouts (Club/Business tier) don't belong on the player dashboard —
    // send them to their workspace: the Player Database search.
    if (user?.is_paid_scout) {
      navigate("/players-database", { replace: true });
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscription return handler — Stripe redirects back here after
  // checkout success with `?subscribe_session=cs_xxx`. We poll the
  // status endpoint (max ~10s) which idempotently persists the
  // subscription on the user record and surfaces a success toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subSession = params.get("subscribe_session");
    if (subSession) {
      params.delete("subscribe_session");
      const newSearch = params.toString();
      navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" }, { replace: true });

      const poll = async (attempts = 0) => {
        if (attempts > 5) {
          toast.info("Subscription confirmation taking a bit longer — refresh in a moment.", { duration: 8000 });
          return;
        }
        try {
          const { data } = await api.get(`/payments/subscribe/status/${subSession}`);
          if (data.payment_status === "paid") {
            trackPurchase();
            setSubscription(data.subscription);
            toast.success(`Welcome to ${data.tier === "vip" ? "VIP Premium" : "Premium"}! Your subscription is now active.`, { duration: 9000 });
            return;
          }
          setTimeout(() => poll(attempts + 1), 2000);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't confirm subscription.");
        }
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // One-shot first-visit pulse on the locked Unlock pills, à la Linear /
  // Stripe Express.
  const [unlockPulseOn, setUnlockPulseOn] = useState(false);
  useEffect(() => {
    if (passState?.active) return; // premium user — no upgrade hint needed
    if (!players?.length) return;
    const key = "dashboard_unlock_pulse_seen_v1";
    try {
      if (window.localStorage.getItem(key)) return;
      setUnlockPulseOn(true);
      window.localStorage.setItem(key, "1");
      const t = setTimeout(() => setUnlockPulseOn(false), 1900);
      return () => clearTimeout(t);
    } catch {
      // localStorage unavailable (private mode, etc.) — silently skip the hint
    }
  }, [passState?.active, players?.length]);

  // ─── derived stats ───
  const totalReports = reports.length;
  const premiumReports = reports.filter((r) => r.is_paid || r.manually_unlocked).length;
  const trackedPlayers = players.length;
  const lastReport = reports[0]; // assumed sorted desc by API
  const planLabel = subscription?.tier
    ? subscription.tier === "vip"
      ? "VIP Premium"
      : "Premium"
    : passState?.active
    ? "Progress Pass"
    : premiumReports > 0
    ? "Pay-per-report"
    : "Free";
  // Premium access (dashboard-wide): active subscription, legacy pass,
  // at least one unlocked report, or admin.
  const premiumAccess =
    !!subscription?.tier || !!passState?.active || premiumReports > 0 || user?.role === "admin";
  const heroTier = subscription?.tier === "vip" ? "vip" : premiumAccess ? "premium" : "free";

  const scrollToPlans = () => {
    const el =
      document.querySelector('[data-testid="dashboard-upgrade-banner"]') ||
      document.querySelector('[data-testid="dashboard-subscription-card"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen bg-cream-base text-ink">
      <Navigation />
      <div className="pt-20 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <DashboardHero user={user} tier={heroTier} />

          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-forest mx-auto" />
            </div>
          ) : (
            <>
              {/* PROGRESS PASS BANNER (legacy holders only) */}
              {passState?.active && (
                <LegacyPassActiveBanner passState={passState} />
              )}

              {/* SUBSCRIPTION CARD (Premium / VIP) — only when user has one */}
              <SubscriptionCard
                subscription={subscription}
                tiers={tiers}
                onChange={(s) => setSubscription(s)}
              />

              {/* FREE MEMBERSHIP BAND — free users only */}
              {!premiumAccess && (
                <FreeMembershipBand totalReports={totalReports} onSeePlans={scrollToPlans} />
              )}

              {/* QUICK STATS ROW */}
              <div
                data-testid="dashboard-quick-stats"
                className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
              >
                <QuickStatTile
                  icon={Film}
                  label="Reports created"
                  value={totalReports}
                  sub={totalReports === 0 ? "Your journey starts with one video" : null}
                  testid="qs-uploads"
                />
                <QuickStatTile
                  icon={CheckCircle2}
                  label="Premium reports"
                  value={premiumReports}
                  sub={!premiumAccess ? "Scouts only see premium reports" : null}
                  accent
                  testid="qs-premium"
                />
                <QuickStatTile
                  icon={Eye}
                  label="Profile views"
                  value={profileViews?.locked ? "—" : (profileViews?.total ?? 0)}
                  sub={
                    profileViews?.locked
                      ? "Scouts can't see you on Free — upgrade to be visible"
                      : profileViews?.this_week > 0
                      ? `+${profileViews.this_week} this week`
                      : profileViews?.discoverable
                      ? "Visible in the Scout Library"
                      : "Turn on visibility to be found"
                  }
                  testid="qs-views"
                />
                <QuickStatTile
                  icon={planLabel === "VIP Premium" ? Crown : planLabel === "Premium" ? TrendingUp : Sparkles}
                  label="Current plan"
                  value={planLabel}
                  small
                  testid="qs-plan"
                />
              </div>

              {/* Last activity meta */}
              {lastReport && (
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] font-bold text-ink/50">
                  Last upload ·{" "}
                  <span className="text-forest">
                    {lastReport.player_details?.player_name || "—"}
                  </span>{" "}
                  · {new Date(lastReport.created_at).toLocaleDateString()}
                </p>
              )}

              {/* PERFORMANCE HQ (premium) or SCOUT PREVIEW UPSELL (free) */}
              {premiumAccess ? (
                <PerformancePanel />
              ) : (
                <ScoutPreviewUpsell
                  playersCount={community?.enabled !== false ? community?.players_in_library : null}
                  onSeePlans={scrollToPlans}
                />
              )}

              {/* UPGRADE BANNER — free users + subscribers at their monthly limit */}
              {(() => {
                const isPremiumAtLimit =
                  subscription?.tier === "premium" && usage?.exhausted;
                const isVipAtLimit =
                  subscription?.tier === "vip" && usage?.exhausted;
                const showBanner =
                  (!subscription?.tier && !passState?.active) ||
                  (isPremiumAtLimit && !passState?.active) ||
                  (isVipAtLimit && !passState?.active);
                if (!showBanner) return null;
                const mode = isVipAtLimit
                  ? "vip-at-limit"
                  : isPremiumAtLimit
                  ? "premium-at-limit"
                  : "free";
                return (
                  <UpgradeBanner
                    tiers={tiers}
                    mode={mode}
                    usage={usage}
                    latestLockedReportId={reports.find((r) => !(r.is_paid || r.manually_unlocked))?.id}
                  />
                );
              })()}

              {/* REPORTS (Library) */}
              <section className="mt-10">
                <SectionHeader
                  icon={Film}
                  eyebrow="Library"
                  title="Your reports"
                  countLabel={reports.length > 0 ? `${reports.length} ${reports.length === 1 ? "report" : "reports"}` : null}
                />
                {reports.length === 0 ? (
                  <div className="mt-4 border border-ink/10 bg-cream-card p-12 text-center">
                    <Film className="w-12 h-12 text-forest mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="font-barlow font-black uppercase text-2xl">Your story starts here</h3>
                    <p className="mt-2 text-ink/65 text-sm">Upload your first football video and receive an instant free scout preview — the first step to being seen.</p>
                    <Link
                      to="/upload"
                      data-testid="dashboard-empty-upload-btn"
                      className="inline-flex mt-6 bg-forest hover:bg-forest-pop text-white font-barlow font-black uppercase tracking-widest text-sm px-6 py-3 transition-colors items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Upload video
                    </Link>
                  </div>
                ) : (
                  <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                    {reports.map((r) => {
                      const unlocked = r.is_paid || r.manually_unlocked;
                      return (
                        <div key={r.id} className="group bg-cream-card hover:bg-white transition-colors flex flex-col overflow-hidden">
                        <Link
                          to={`/report/${r.id}`}
                          data-testid={`dashboard-report-${r.id}`}
                          data-report-id={r.id}
                          className="flex-1 flex flex-col overflow-hidden open-report-link"
                        >
                          <div className="relative aspect-video bg-ink overflow-hidden">
                            {r.poster_url ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${r.poster_url}`}
                                alt={r.player_details?.player_name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ink to-forest/40">
                                <Film className="w-10 h-10 text-white/40" strokeWidth={1.5} />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink bg-cream-card/90 backdrop-blur px-2 py-1">
                                {new Date(r.created_at).toLocaleDateString()}
                              </span>
                              {unlocked ? (
                                <span className="flex items-center gap-1 bg-forest text-white text-[10px] uppercase tracking-widest font-black px-2 py-1">
                                  <CheckCircle2 className="w-3 h-3" /> Premium
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 bg-cream-card/90 backdrop-blur border border-ink/15 text-ink/80 text-[10px] uppercase tracking-widest font-bold px-2 py-1">
                                  <Lock className="w-3 h-3" /> Preview
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="px-4 pt-3 pb-3.5 flex-1 flex flex-col">
                            <div className="flex items-start justify-between gap-3">
                              <h3 className="font-barlow font-black uppercase text-lg text-ink group-hover:text-forest transition-colors leading-tight truncate">
                                {r.player_details?.player_name}
                              </h3>
                              <span className="text-forest text-[10px] uppercase tracking-widest font-black flex-shrink-0 mt-1">View →</span>
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <MiniPitch position={r.player_details?.position} className="w-5 h-7 flex-shrink-0" />
                              <p className="text-[12px] text-ink/60 leading-snug">
                                {r.player_details?.position} · age {r.player_details?.age}
                                {r.player_details?.video_type ? <span className="text-ink/40"> · {r.player_details.video_type}</span> : null}
                              </p>
                            </div>
                          </div>
                        </Link>
                        {r.video_url && (
                          <VideoShareRow
                            report={r}
                            onChanged={(id, val) =>
                              setReports((prev) => prev.map((x) => (x.id === id ? { ...x, video_share_enabled: val } : x)))
                            }
                          />
                        )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* NOTIFICATIONS + MESSAGES */}
              <div data-testid="dashboard-inbox-anchor">
                <InboxPanels onUpgrade={scrollToPlans} />
              </div>

              {/* SCOUTMEPLAY NETWORK (admin-controlled numbers) */}
              <CommunityPulse data={community} />

              {/* TRIALS & OPPORTUNITIES — premium only */}
              {premiumAccess && <OpportunitiesPanel />}

              {/* PROFILE & VISIBILITY */}
              <ProfileVisibilityCard latestReportId={reports[0]?.id} premiumAccess={premiumAccess} />

              <ReferralInviteCard />

              <FeatureConsentCard />

              {reports.length > 0 && <ReviewPrompt />}

              {/* PLAYERS / TRAJECTORIES */}
              {players.length > 0 && (
                <section className="mt-12" data-testid="dashboard-players-section">
                  <SectionHeader
                    icon={Activity}
                    eyebrow="Track progress"
                    title="Your players"
                    countLabel={`${players.length} tracked`}
                  />
                  <ul className="mt-3 bg-cream-card border border-ink/10 divide-y divide-ink/10">
                    {players.map((p) => (
                      <PlayerRow
                        key={p.id}
                        p={p}
                        isPremium={!!subscription?.tier || !!passState?.active}
                        onUpgradeClick={scrollToPlans}
                        pulse={unlockPulseOn}
                      />
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── FreeMembershipBand — the FREE-member card from the approved mockup:
 *    membership ring + free-upload tracker + "Unlock your full potential". */
function FreeMembershipBand({ totalReports, onSeePlans }) {
  const used = Math.min(totalReports, 1);
  const left = 1 - used;
  return (
    <div
      data-testid="free-membership-band"
      className="mt-8 bg-cream-card border border-gray-border rounded-3xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-7"
    >
      <div className="flex items-center gap-5">
        <div className="w-[96px] h-[96px] rounded-full border-[6px] border-forest/25 bg-forest/5 flex flex-col items-center justify-center shrink-0">
          <span className="font-barlow font-black text-lg text-forest leading-none">FREE</span>
          <span className="text-[10px] font-bold text-forest/80 tracking-wider">MEMBER</span>
        </div>
        <div>
          <b className="text-base text-ink" data-testid="free-band-upload-status">
            {left > 0 ? "You have 1 free upload" : "Free preview used"}
          </b>
          <div className="mt-2 flex items-center gap-2.5 text-[13px] font-bold text-ink/60">
            {used}
            <span className="w-[160px] h-2 rounded-full bg-ink/10 overflow-hidden">
              <span className="block h-full rounded-full bg-forest" style={{ width: `${used * 100 || 4}%` }} />
            </span>
            1
          </div>
          <p className="mt-1.5 text-[12px] text-ink/50">
            {left > 0 ? "1 upload left — make it count" : "Unlock premium to keep growing"}
          </p>
        </div>
      </div>
      <div className="hidden md:block w-px self-stretch bg-ink/10" aria-hidden />
      <div className="flex-1">
        <b className="text-base text-ink">Unlock your full potential</b>
        <p className="mt-1 text-[13px] text-ink/60 max-w-sm">
          Complete analysis, PDF scout reports, benchmarks, Scout Library visibility and messages from agents &amp; scouts.
        </p>
        <button
          type="button"
          onClick={onSeePlans}
          data-testid="free-band-see-plans-btn"
          className="mt-3 inline-flex items-center gap-2 bg-forest hover:bg-forest-pop text-white font-barlow font-black uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors"
        >
          See plans <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── VideoShareRow — parent-consent toggle for permanent video links. */
function VideoShareRow({ report, onChanged }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const enabled = !!report.video_share_enabled;
  const shareUrl = report.video_url
    ? `${process.env.REACT_APP_BACKEND_URL}${report.video_url.split("?")[0]}`
    : null;

  const setShare = async (val) => {
    setBusy(true);
    try {
      await api.post(`/reports/${report.id}/video-share`, { enabled: val });
      onChanged(report.id, val);
      setConfirming(false);
      toast.success(
        val
          ? "Sharing is ON — anyone with the link can watch this video."
          : "Sharing turned off — the shared link no longer works."
      );
    } catch {
      toast.error("Could not update sharing — try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Video link copied — it works permanently until you turn sharing off.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  return (
    <div className="border-t border-ink/10 px-4 py-2.5 bg-cream-soft/40" data-testid={`share-row-${report.id}`}>
      {confirming ? (
        <div>
          <p className="text-[11px] text-ink/70 leading-snug">
            <span className="font-black text-ink">Share this video permanently?</span>{" "}
            Anyone with the link can watch it — until you turn sharing off again.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              data-testid={`share-confirm-${report.id}`}
              disabled={busy}
              onClick={() => setShare(true)}
              className="bg-forest hover:bg-forest-pop text-white text-[10px] uppercase tracking-widest font-black px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              {busy ? "…" : "Yes, share"}
            </button>
            <button
              type="button"
              data-testid={`share-cancel-${report.id}`}
              disabled={busy}
              onClick={() => setConfirming(false)}
              className="text-[10px] uppercase tracking-widest font-bold text-ink/50 hover:text-ink px-2 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-black text-ink/60 min-w-0">
            {enabled ? (
              <>
                <Globe2 className="w-3.5 h-3.5 text-forest flex-shrink-0" />
                <span className="text-forest">Video link: shared</span>
              </>
            ) : (
              <>
                <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                Video link: private
              </>
            )}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {enabled && shareUrl && (
              <button
                type="button"
                data-testid={`share-copy-${report.id}`}
                onClick={copyLink}
                className="flex items-center gap-1 border border-forest/30 text-forest hover:bg-forest hover:text-white text-[10px] uppercase tracking-widest font-black px-2 py-1 transition-colors"
              >
                <Copy className="w-3 h-3" /> Copy link
              </button>
            )}
            <button
              type="button"
              data-testid={`share-toggle-${report.id}`}
              disabled={busy}
              onClick={() => (enabled ? setShare(false) : setConfirming(true))}
              className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 transition-colors disabled:opacity-50 ${
                enabled
                  ? "text-ink/50 hover:text-ink border border-ink/15"
                  : "bg-forest hover:bg-forest-pop text-white"
              }`}
            >
              {busy ? "…" : enabled ? "Turn off" : "Share"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Shared panel header used by dashboard sections. */
function SectionHeader({ icon: Icon, eyebrow, title, countLabel }) {
  return (
    <header className="relative">
      <div className="flex items-end justify-between gap-3 pb-3">
        <div className="flex items-baseline gap-2.5 min-w-0">
          {Icon && (
            <Icon
              className="w-3.5 h-3.5 text-forest flex-shrink-0 self-center -mt-0.5"
              strokeWidth={2.4}
              aria-hidden="true"
            />
          )}
          <span className="text-forest text-[10px] uppercase tracking-[0.3em] font-black flex-shrink-0">
            {eyebrow}
          </span>
          <span className="h-3 w-px bg-ink/25 flex-shrink-0" aria-hidden="true" />
          <h2 className="font-barlow font-black uppercase text-xl md:text-2xl tracking-tighter leading-none truncate">
            {title}
          </h2>
        </div>
        {countLabel && (
          <span className="text-[10px] uppercase tracking-widest text-ink/55 font-bold flex-shrink-0">
            {countLabel}
          </span>
        )}
      </div>
      {/* 2-tone hairline accent rule */}
      <div className="relative h-px w-full bg-ink/15" aria-hidden="true">
        <span className="absolute left-0 top-0 h-px w-12 bg-forest" />
      </div>
    </header>
  );
}

/* ── Compact row in the "Your players" list. */
function PlayerRow({ p, isPremium, onUpgradeClick, pulse = false }) {
  return (
    <li className="relative">
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 bottom-0 w-[3px] ${
          isPremium ? "bg-forest" : "bg-ink/15"
        }`}
      />
      <Link
        to={`/trajectory/${p.id}`}
        data-testid={`player-card-${p.id}`}
        className="group flex items-center gap-3 sm:gap-4 pl-5 pr-4 py-3.5 hover:bg-cream-soft/60 transition-colors"
      >
        <div className="flex-shrink-0 min-w-[88px] sm:min-w-[124px]">
          <span className="inline-flex bg-forest/10 text-forest font-barlow font-black uppercase text-[10px] tracking-[0.18em] px-2 py-1 leading-none">
            {p.last_position || "Player"}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-barlow font-black uppercase text-base sm:text-lg text-ink group-hover:text-forest transition-colors leading-tight truncate">
            {p.name}
          </h3>
          <p className="mt-0.5 text-[11px] text-ink/55 leading-tight truncate">
            {p.last_age ? `Age ${p.last_age}` : ""}
            {p.preferred_foot ? ` · ${p.preferred_foot} foot` : ""}
            {` · ${p.report_count} ${p.report_count === 1 ? "report" : "reports"}`}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {isPremium ? (
            <>
              <span className="hidden sm:flex items-center gap-1 bg-forest text-white text-[9px] uppercase tracking-widest font-black px-2 py-1">
                <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} /> Premium
              </span>
              <CheckCircle2 className="sm:hidden w-4 h-4 text-forest" strokeWidth={2.4} aria-hidden="true" />
            </>
          ) : (
            <>
              <span aria-hidden="true" className="hidden sm:inline-block w-[78px] h-[22px]" />
              <span aria-hidden="true" className="sm:hidden inline-block w-7 h-7" />
            </>
          )}
          <ArrowRight className="w-4 h-4 text-forest group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>
      {!isPremium && onUpgradeClick && (
        <>
          <button
            type="button"
            onClick={onUpgradeClick}
            data-testid={`player-row-upgrade-${p.id}`}
            aria-label={`Upgrade to unlock progress tracking for ${p.name}`}
            title="Upgrade to unlock"
            className={`${pulse ? "scoutme-unlock-pulse " : ""}hidden sm:flex absolute z-10 right-9 top-1/2 -translate-y-1/2 items-center gap-1 bg-cream-soft hover:bg-forest hover:text-white hover:border-forest border border-ink/15 text-ink/65 text-[9px] uppercase tracking-widest font-bold px-2 py-1 transition-colors cursor-pointer`}
          >
            <Lock className="w-3 h-3" strokeWidth={2.4} /> Unlock
          </button>
          <button
            type="button"
            onClick={onUpgradeClick}
            data-testid={`player-row-upgrade-mobile-${p.id}`}
            aria-label={`Upgrade to unlock progress tracking for ${p.name}`}
            className={`${pulse ? "scoutme-unlock-pulse " : ""}sm:hidden absolute z-10 right-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 text-ink/45 hover:text-forest active:text-forest transition-colors cursor-pointer`}
          >
            <Lock className="w-3.5 h-3.5" strokeWidth={2.4} />
          </button>
        </>
      )}
    </li>
  );
}

/* ── QuickStatTile — KPI tile in the top stats row. */
function QuickStatTile({ icon: Icon, label, value, sub = null, accent = false, small = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative bg-cream-card border rounded-2xl ${
        accent ? "border-forest/40" : "border-gray-border"
      } p-4 md:p-5 flex items-start gap-3 md:gap-4 hover:border-forest/50 transition-colors`}
    >
      <span
        className={`shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center border ${
          accent ? "bg-forest text-white border-forest" : "bg-forest/10 text-forest border-forest/20"
        }`}
      >
        <Icon className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <div
          className={`font-barlow font-black uppercase leading-none text-ink ${
            small ? "text-base md:text-lg tracking-tight" : "text-2xl md:text-3xl tracking-tighter"
          }`}
        >
          {value}
        </div>
        <div className="mt-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-bold text-ink/55">
          {label}
        </div>
        {sub && (
          <div className="mt-1 text-[10px] text-ink/45 leading-snug normal-case tracking-normal font-medium">
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── LEGACY PROGRESS PASS BANNER — legacy $399 pass holders only. */
function LegacyPassActiveBanner({ passState }) {
  if (!passState?.active) return null;
  return (
    <div data-testid="progress-pass-active-banner" className="mt-10 bg-forest text-white p-5 md:p-6 grid md:grid-cols-3 gap-4 items-center border-l-8 border-forest-pop">
      <div className="md:col-span-2 flex items-center gap-3">
        <div className="w-11 h-11 bg-white/10 border border-white/30 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">Progress Pass active</div>
          <h2 className="font-barlow font-black uppercase text-xl tracking-tighter">
            {passState.credits_remaining} of {passState.credits_total} report credits remaining
          </h2>
          {passState.expires_at && (
            <p className="text-xs opacity-80 mt-1">
              Expires {new Date(passState.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      <div className="flex md:justify-end">
        <Link to="/upload" data-testid="progress-pass-use-credit-btn" className="inline-flex items-center gap-2 bg-white text-forest hover:bg-cream-soft font-barlow font-black uppercase tracking-widest text-xs px-5 py-3 transition-colors">
          Use a credit <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}


/* ── UPGRADE BANNER — free users + subscribers at their monthly limit. */
function UpgradeBanner({ tiers, mode = "free", usage = null, latestLockedReportId = null }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // "premium" | "vip" | "extra" | null
  const isPremiumAtLimit = mode === "premium-at-limit";
  const isVipAtLimit = mode === "vip-at-limit";
  const isAtLimit = isPremiumAtLimit || isVipAtLimit;
  const extraPrice = usage?.extra_report_price;
  const extraTierLabel = isVipAtLimit ? "VIP" : "Premium";

  const startSubscription = (tier) => navigate(`/checkout/${tier}`);

  const buyExtraReport = () => navigate("/checkout/single");

  const vip = tiers?.vip || { amount: 49.99 };
  const monthlyLimit = usage?.monthly_limit ?? (isVipAtLimit ? 4 : 2);
  const usedThisPeriod = usage?.used_this_period ?? monthlyLimit;

  return (
    <div
      data-testid={
        isVipAtLimit
          ? "dashboard-upgrade-banner-vip-at-limit"
          : isPremiumAtLimit
          ? "dashboard-upgrade-banner-at-limit"
          : "dashboard-upgrade-banner"
      }
      className={`mt-10 relative overflow-hidden p-5 md:p-7 ${
        isAtLimit ? "border border-[#F5C443]/25 text-white" : "bg-cream-card border border-gray-border"
      }`}
    >
      {isAtLimit && (
        <>
          <div aria-hidden className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/assets/premium-dash-gold.jpg)" }} />
          <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#06120B]/[0.97] via-[#06120B]/90 to-[#06120B]/75" />
          <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F5C443]/70 to-transparent" />
        </>
      )}
      <div className="relative flex items-center gap-2 mb-3">
        <span aria-hidden className="relative flex items-center justify-center w-2 h-2 shrink-0">
          <span className={`absolute inset-0 rounded-full ${isAtLimit ? "bg-[#F5C443]" : "bg-volt"} animate-ping opacity-75`} />
          <span className={`relative rounded-full w-1.5 h-1.5 ${isAtLimit ? "bg-[#F5C443]" : "bg-volt"}`} />
        </span>
        <span className={`text-[10px] uppercase tracking-[0.28em] font-bold ${isAtLimit ? "text-[#F5C443]" : "text-forest"}`}>
          {isAtLimit
            ? `${extraTierLabel} quota · ${usedThisPeriod} / ${monthlyLimit} reports used this month`
            : "Unlock your full potential"}
        </span>
      </div>
      <h2 className={`relative font-barlow font-black uppercase text-2xl md:text-3xl tracking-tighter leading-[0.95] ${isAtLimit ? "text-white" : "text-ink"}`}>
        {isAtLimit ? (
          <>Keep the momentum.<br /><span className="bg-gradient-to-r from-[#F5C443] to-[#FFE08A] bg-clip-text text-transparent">Add one extra report{extraPrice ? <> · <span className="text-[#CCFF00]">${extraPrice}</span></> : null}</span></>
        ) : (
          <>Ready for more?<br /><span className="text-forest">Upgrade your plan.</span></>
        )}
      </h2>
      <p className={`relative mt-2 text-sm max-w-xl ${isAtLimit ? "text-white/70" : "text-ink/65"}`}>
        {isVipAtLimit
          ? `All ${monthlyLimit} VIP reports used — that's elite-level commitment. Add one extra at your private VIP rate and keep the scouts watching. Quota resets next billing cycle.`
          : isPremiumAtLimit
          ? `All ${monthlyLimit} Premium reports used — proof you're putting in the work. Add one extra at your private member rate, or step up to VIP for 4 reports a month and a real scout's eyes on your game.`
          : "You're on the Free plan. Upgrade for more uploads, advanced analysis, and (with VIP) a real scout reviewing your video."}
      </p>

      {isAtLimit ? (
        <div className={`relative mt-5 grid gap-3 ${isPremiumAtLimit ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
          {/* Buy-1-extra-report card — primary action for at-limit subscribers */}
          <button
            type="button"
            data-testid="dashboard-buy-extra-report-btn"
            onClick={buyExtraReport}
            disabled={busy !== null}
            className="relative text-left bg-[#0F3A22] border border-forest p-5 hover:shadow-[0_18px_36px_-12px_rgba(15,58,34,0.55)] transition-all disabled:opacity-60 disabled:cursor-wait"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="inline-flex items-center gap-1.5 text-[#CCFF00] text-[10px] uppercase tracking-[0.18em] font-black">
                <TrendingUp className="w-3.5 h-3.5" /> Extra report
              </span>
              <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#A5DD5F]/80 bg-[#A5DD5F]/10 px-2 py-0.5 rounded-full">
                Cheapest for you
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-barlow font-black text-3xl text-[#CCFF00]">${extraPrice ?? (isVipAtLimit ? 59 : 89)}</span>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/70">/ one report</span>
            </div>
            <ul className="mt-3 space-y-1.5 text-[12px] text-white/85 leading-snug">
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#A5DD5F]" /> Same full 4-pillar premium report</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#A5DD5F]" /> Cheaper than the single-report price</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#A5DD5F]" /> Subscription stays untouched</li>
            </ul>
            <span className="mt-4 inline-flex items-center gap-1.5 bg-[#A5DD5F] text-[#0F3A22] font-barlow font-black uppercase tracking-[0.18em] text-xs px-4 py-2">
              {busy === "extra" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Buy 1 report <ArrowRight className="w-3.5 h-3.5" /></>}
            </span>
          </button>

          {/* VIP upgrade — only offered to Premium-at-limit users */}
          {isPremiumAtLimit && (
            <button
              type="button"
              data-testid="dashboard-upgrade-vip-btn"
              onClick={() => startSubscription("vip")}
              disabled={busy !== null}
              className="relative text-left bg-[#0A0F0D] border border-[#1F2724] p-5 hover:shadow-[0_18px_36px_-12px_rgba(0,0,0,0.65)] transition-all disabled:opacity-60 disabled:cursor-wait"
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="inline-flex items-center gap-1.5 text-[#F5C443] text-[10px] uppercase tracking-[0.18em] font-black">
                  <Crown className="w-3.5 h-3.5" fill="#F5C443" /> Upgrade to VIP
                </span>
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#F5C443]/80 bg-[#F5C443]/10 px-2 py-0.5 rounded-full">
                  Best value
                </span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-barlow font-black text-3xl text-[#F5C443]">${vip.amount?.toFixed(2)}</span>
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/70">/ month</span>
              </div>
              <ul className="mt-3 space-y-1.5 text-[12px] text-white/85 leading-snug">
                <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#F5C443]" /> 4 reports per month included</li>
                <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#F5C443]" /> Real scout review + direct contact</li>
                <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#F5C443]" /> Deeper per-report discount</li>
              </ul>
              <span className="mt-4 inline-flex items-center gap-1.5 bg-[#F5C443] text-[#0A0F0D] font-barlow font-black uppercase tracking-[0.18em] text-xs px-4 py-2">
                {busy === "vip" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Go VIP <ArrowRight className="w-3.5 h-3.5" /></>}
              </span>
            </button>
          )}
        </div>
      ) : (
        <div className="relative mt-6">
          {/* Unified 3-tier pricing (Single / Premium / VIP) */}
          <DreamPricingTiers
            isLoggedIn
            onUnlockSingle={() => {
              if (latestLockedReportId) navigate(`/report/${latestLockedReportId}?unlock=1`);
              else navigate("/upload");
            }}
          />
        </div>
      )}

      {isAtLimit && (
        <p className="relative mt-3 text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-1.5 text-white/50">
          <Lock className="w-3 h-3 text-[#F5C443]" />
          Secure Stripe · Cancel anytime from your dashboard
        </p>
      )}
    </div>
  );
}


/* ── SUBSCRIPTION CARD — the user's active monthly plan (Premium / VIP). */
function SubscriptionCard({ subscription, tiers, onChange }) {
  const [busy, setBusy] = useState(null); // "cancel" | "resume" | "change" | null

  if (!subscription || !subscription.tier) return null;

  const tier = subscription.tier;
  const conf = tiers[tier] || {};
  const isVip = tier === "vip";
  const otherTier = isVip ? "premium" : "vip";
  const otherConf = tiers[otherTier] || {};
  const willCancel = !!subscription.cancel_at_period_end;
  const periodEnd = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;
  const isActive = ["active", "trialing", "past_due"].includes(subscription.status);

  const doAction = async (action) => {
    if (busy) return;
    setBusy(action);
    try {
      let res;
      if (action === "cancel") res = await api.post("/me/subscription/cancel");
      else if (action === "resume") res = await api.post("/me/subscription/resume");
      else if (action === "change") res = await api.post("/me/subscription/change-tier", { tier: otherTier, origin_url: window.location.origin });
      onChange(res.data.subscription);
      toast.success(
        action === "cancel" ? `Cancellation scheduled. Access continues until ${periodEnd}.` :
        action === "resume" ? "Subscription reactivated." :
        `Plan changed to ${otherTier === "vip" ? "VIP Premium" : "Premium"} — proration applied on next invoice.`,
        { duration: 8000 }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.", { duration: 7000 });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="dashboard-subscription-card"
      className="mt-10 relative overflow-hidden border border-[#F5C443]/25 text-white"
    >
      {/* Nano Banana gold pitch texture + tier-tinted overlay */}
      <div aria-hidden className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/assets/premium-dash-gold.jpg)" }} />
      <div aria-hidden className={`absolute inset-0 ${isVip ? "bg-gradient-to-r from-[#050807]/[0.97] via-[#050807]/90 to-[#050807]/70" : "bg-gradient-to-r from-[#06180E]/[0.97] via-[#06180E]/90 to-[#06180E]/70"}`} />
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F5C443]/70 to-transparent" />

      <div className="relative p-6 md:p-8 grid md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5 mb-2.5">
            <span className="w-8 h-8 flex items-center justify-center border border-[#F5C443]/40 bg-[#F5C443]/10">
              <Crown className="w-4 h-4 text-[#F5C443]" fill="#F5C443" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] font-black text-[#F5C443]">
              Active plan · {subscription.status}
            </span>
          </div>
          <h3 className="font-barlow font-black uppercase text-3xl md:text-4xl tracking-tight leading-none">
            {isVip
              ? <>VIP <span className="bg-gradient-to-r from-[#F5C443] to-[#FFE08A] bg-clip-text text-transparent">Premium</span></>
              : <>Premium <span className="text-[#CCFF00]">Member</span></>}
          </h3>
          <p className="mt-2 text-sm text-white/70">
            ${conf.amount?.toFixed(2) ?? "—"} / month — full scout-grade analysis on every upload.
          </p>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {(isVip
              ? ["4 reports / month", "Real scout review", "Direct contact", "Deepest discount"]
              : [`${conf.monthly_upload_limit ?? "—"} reports / month`, "Advanced analysis", "Full premium dossier"]
            ).map((perk) => (
              <span key={perk} className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[0.06] px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-white/80">
                <CheckCircle2 className={`w-3 h-3 ${isVip ? "text-[#F5C443]" : "text-[#CCFF00]"}`} /> {perk}
              </span>
            ))}
          </div>
          {periodEnd && (
            <p className={`mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold px-2.5 py-1 ${
              willCancel ? "bg-amber-500/20 text-amber-200" : "bg-white/10 text-white/85"
            }`}>
              <Calendar className="w-3 h-3" />
              {willCancel ? `Ends on ${periodEnd}` : `Next billing ${periodEnd}`}
            </p>
          )}
        </div>

      <div className="flex flex-col gap-2 md:items-end">
        {isActive && !willCancel && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => doAction("change")}
            data-testid="subscription-change-tier-btn"
            className={`group w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 font-barlow font-black uppercase tracking-[0.16em] text-xs transition-all disabled:opacity-60 disabled:cursor-wait ${
              isVip
                ? "bg-white/10 hover:bg-white/15 text-white border border-white/20"
                : "bg-[#F5C443] hover:bg-[#FFD661] text-[#0A0F0D]"
            }`}
          >
            {busy === "change" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                {isVip ? "Switch to Premium" : "Upgrade to VIP"}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
        {isActive && !willCancel && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => doAction("cancel")}
            data-testid="subscription-cancel-btn"
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 text-white/70 hover:text-white text-xs uppercase tracking-[0.18em] font-bold transition-colors disabled:opacity-60"
          >
            {busy === "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
              <><XCircle className="w-3.5 h-3.5" /> Cancel subscription</>
            )}
          </button>
        )}
        {willCancel && isActive && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => doAction("resume")}
            data-testid="subscription-resume-btn"
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#CCFF00] hover:bg-[#D8FF33] text-[#0F3A22] font-barlow font-black uppercase tracking-[0.16em] text-xs transition-all disabled:opacity-60 disabled:cursor-wait"
          >
            {busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><RefreshCw className="w-4 h-4" /> Reactivate</>
            )}
          </button>
        )}
        {otherConf?.amount && isActive && !willCancel && (
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold md:text-right">
            ${otherConf.amount.toFixed(2)}/mo · proration applied
          </p>
        )}
        </div>
      </div>
    </div>
  );
}
